import { Link } from "react-router-dom";
import { useState } from "react";
import SubsWall, { STYLE_PALETTES, type SubTile, type TileStyle } from "../components/SubsWall";
import "./LandingV2.css";

// ----------------------------------------------------------------------------
// Hero wall — 8 curated supporters across all 5 Atelier tile styles.
// Lives in the hero so the product sells itself by showing what it is.
// ----------------------------------------------------------------------------

function makeHeroTiles(): SubTile[] {
  const seed: Array<Partial<SubTile> & { name: string; message: string; monthlyCents: number; style: TileStyle }> = [
    { name: "Sarah K.",        message: "Your masterclass changed my career.",                       monthlyCents: 500,   style: "card" },
    { name: "Marcus T.",       message: "Two years and counting. Worth every cent.",                 monthlyCents: 500,   style: "card" },
    { name: "Hiro Tanaka",     message: "The camera reviews are why I bought the GFX.",              monthlyCents: 1000,  style: "inked" },
    { name: "Lena R.",         message: "I quote you in my own lectures.",                           monthlyCents: 1000,  style: "inked" },
    { name: "Dr. Owen Hayes",  message: "Reading you at 6am with coffee.",                           monthlyCents: 2500,  style: "signature", anniversaryMonths: 14 },
    { name: "M.J. Asante",     message: "Long-form journalism, refound.",                            monthlyCents: 5000,  style: "gilded", anniversaryMonths: 26 },
    { name: "Helena Voss",     message: "Quality compounds. Patron since '23.",                      monthlyCents: 10000, style: "gilded", anniversaryMonths: 30 },
    { name: "James Lockwood",  message: "Founding member · supporting since day one.",               monthlyCents: 25000, style: "engraved", founderNumber: 1, anniversaryMonths: 36 },
  ];
  return seed.map((s, i) => ({
    id: `hero-${i}`,
    name: s.name,
    message: s.message,
    style: s.style,
    color: STYLE_PALETTES[s.style][(i + 2) % 8],
    xPct: 14 + ((i * 73) % 74),
    yPct: 22 + ((i * 41) % 64),
    monthlyCents: s.monthlyCents,
    anniversaryMonths: s.anniversaryMonths ?? 0,
    founderNumber: s.founderNumber ?? null,
    boostUntil: null,
    fadeStartedAt: null,
    badge: null,
  }));
}

export default function LandingV2(): JSX.Element {
  const [tiles] = useState<SubTile[]>(makeHeroTiles);

  return (
    <div className="at-page lv2">
      {/* Top nav — thin, magazine-style */}
      <nav className="lv2-nav at-container">
        <div className="lv2-brand">
          <span className="lv2-brand-mark" aria-hidden="true">◆</span> SubsWall
        </div>
        <div className="lv2-nav-right">
          <a href="#how">How it works</a>
          <a href="#pricing">Pricing</a>
          <Link to="/login">Sign in</Link>
          <Link to="/login" className="at-btn at-btn-primary">Claim your wall</Link>
        </div>
      </nav>

      {/* HERO */}
      <header className="lv2-hero">
        <div className="at-container lv2-hero-inner">
          <div className="lv2-hero-copy">
            <div className="at-eyebrow">For creators who outlast the algorithm</div>
            <h1 className="at-display lv2-h1">
              Your supporters,<br />
              <em>named on your channel</em><br />
              forever.
            </h1>
            <p className="at-lead lv2-lead">
              SubsWall is a public wall of your monthly supporters. Patreon makes you
              run a newsletter. We make your fans famous. Subscribe once, stay named
              for as long as you support — it's that simple.
            </p>
            <div className="lv2-hero-ctas">
              <Link to="/login" className="at-btn at-btn-primary">Claim your wall →</Link>
              <Link to="/demo" className="at-btn at-btn-ghost">See a live wall</Link>
            </div>
            <div className="lv2-hero-trust">
              <span><strong>4.9%</strong> flat · no hidden fees</span>
              <span>·</span>
              <span>YouTube · Twitch · Podcast · Newsletter</span>
              <span>·</span>
              <span>Cancel anytime</span>
            </div>
          </div>

          <div className="lv2-hero-wall">
            <SubsWall tiles={tiles} theme="atelier" aspectRatio={4 / 3} />
            <div className="lv2-hero-wall-caption">
              A real SubsWall, scaled down. Drag in the editor; readers see it in motion.
            </div>
          </div>
        </div>
      </header>

      {/* THREE-PANEL VALUE PROPOSITION */}
      <section className="at-section lv2-vp" id="how">
        <div className="at-container">
          <div className="at-eyebrow">How it works</div>
          <h2 className="at-display lv2-h2">Three things, no admin work.</h2>

          <div className="lv2-vp-grid">
            <div className="lv2-vp-card">
              <div className="lv2-vp-num">01</div>
              <h3>Claim your wall.</h3>
              <p>
                Sign in with Google or YouTube, pick a handle, link your Stripe.
                Your wall lives at <code>subswall.com/yourname</code>. Embed it in
                your channel page or video descriptions.
              </p>
            </div>
            <div className="lv2-vp-card">
              <div className="lv2-vp-num">02</div>
              <h3>Fans sign in 30 seconds.</h3>
              <p>
                They pick a monthly amount, write their name and a line, choose
                a style, and drag their tile to a spot on your wall. One screen.
                One Stripe checkout. They're on.
              </p>
            </div>
            <div className="lv2-vp-card">
              <div className="lv2-vp-num">03</div>
              <h3>The wall does the rest.</h3>
              <p>
                Cancellation publicly fades over 7 days, so people resub. Tiers
                are visible — your top supporters look unmistakably bigger.
                Anniversaries earn gold dots. No newsletter. No posts. No tax.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="at-section lv2-vs">
        <div className="at-container">
          <div className="at-eyebrow">Why not Patreon</div>
          <h2 className="at-display lv2-h2">Patreon makes you a content factory.<br /><em>We make your fans famous.</em></h2>

          <table className="lv2-vs-table">
            <thead>
              <tr>
                <th></th>
                <th>Patreon</th>
                <th>Buy Me a Coffee</th>
                <th className="lv2-vs-us">SubsWall</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>Recurring revenue</td>           <td>✓</td><td>—</td><td>✓</td></tr>
              <tr><td>Onboarding under 1 minute</td>   <td>—</td><td>✓</td><td>✓</td></tr>
              <tr><td>Fans publicly visible on you</td><td>—</td><td>—</td><td>✓</td></tr>
              <tr><td>Cancellation creates re-sub pull</td><td>—</td><td>—</td><td>✓</td></tr>
              <tr><td>Embed on YouTube channel page</td><td>—</td><td>—</td><td>✓</td></tr>
              <tr><td>Required creator workload</td>   <td>Posts, tiers, DMs</td><td>None</td><td className="lv2-vs-us">None</td></tr>
              <tr><td>Platform fee</td>                <td>8–12%</td><td>5%</td><td className="lv2-vs-us">4.9%</td></tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* PRICING */}
      <section className="at-section lv2-pricing" id="pricing">
        <div className="at-container lv2-pricing-inner">
          <div>
            <div className="at-eyebrow">Pricing</div>
            <h2 className="at-display lv2-h2">One number. <em>4.9%</em> of what you earn.</h2>
            <p className="at-lead">
              Plus Stripe's standard processing (~2.9% + 30¢). No monthly minimum,
              no per-supporter fee, no upsell pricing tiers for creators. You keep
              everything you earn, minus that one line.
            </p>
            <ul className="lv2-pricing-list">
              <li>✓ Unlimited supporters</li>
              <li>✓ Custom domain (Pro plan, coming)</li>
              <li>✓ OBS overlay for streamers</li>
              <li>✓ Embeddable widget for YouTube About pages</li>
              <li>✓ CSV import from BMAC, Ko-fi, Patreon</li>
            </ul>
          </div>
          <div className="lv2-pricing-card">
            <div className="at-eyebrow">Supporter pricing</div>
            <div className="lv2-pricing-ladder">
              <div><span>☕</span> <strong>$5</strong> · a coffee</div>
              <div><span>🍕</span> <strong>$10</strong> · a pizza</div>
              <div><span>🍣</span> <strong>$25</strong> · sushi</div>
              <div><span>🍷</span> <strong>$50</strong> · wine</div>
              <div><span>🍽️</span> <strong>$100</strong> · tasting menu</div>
              <div><span>✈️</span> <strong>$250</strong> · weekend trip</div>
            </div>
            <div className="lv2-pricing-foot">Higher tiers unlock bigger tiles, gold foil, and a permanent place on your channel.</div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="at-section lv2-final">
        <div className="at-container">
          <h2 className="at-display lv2-h2-center">
            Your channel deserves a <em>monument</em>,<br />not a feed.
          </h2>
          <p className="at-lead lv2-final-sub">Take 60 seconds. Claim your handle.</p>
          <div className="lv2-final-ctas">
            <Link to="/login" className="at-btn at-btn-primary">Claim your wall</Link>
            <Link to="/demo" className="at-btn at-btn-ghost">See a live wall</Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="lv2-footer">
        <div className="at-container lv2-footer-inner">
          <div className="lv2-brand">◆ SubsWall · 4.9% take, 95.1% to creators</div>
          <nav className="lv2-footer-nav">
            <Link to="/terms">Terms</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/refunds">Refunds</Link>
            <Link to="/acceptable-use">Acceptable Use</Link>
            <Link to="/contact">Contact</Link>
          </nav>
        </div>
      </footer>
    </div>
  );
}
